import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Lock, LogOut, RefreshCw, ArrowLeft, Package, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "dvf_admin_token";

const inputCls =
  "w-full bg-[#1E1511] border border-[#C85A32]/25 rounded-md px-4 py-3 text-sm text-[#F7F2EB] placeholder-[#E6DACB]/35 outline-none transition-[border-color] duration-300 focus:border-[#E89D4B]";

const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || "").filter(Boolean).join(" ");
  return String(detail);
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [sending, setSending] = useState(false);
  const [quotes, setQuotes] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadQuotes = useCallback(async (tok) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/quotes`, { headers: { Authorization: `Bearer ${tok}` } });
      setQuotes(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        toast.error("Session expired — please sign in again.");
      } else {
        toast.error("Could not load quote requests.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) loadQuotes(token);
  }, [token, loadQuotes]);

  const onLogin = async (e) => {
    e.preventDefault();
    setSending(true);
    setAuthError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
      toast.success("Welcome back, dispatch.");
    } catch (err) {
      setAuthError(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setQuotes(null);
    setEmail("");
    setPassword("");
  };

  if (!token) {
    return (
      <div data-testid="admin-login" className="min-h-screen flex items-center justify-center px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md"
        >
          <Link to="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-[#E6DACB]/60 hover:text-[#E89D4B] transition-colors duration-300 mb-8">
            <ArrowLeft size={13} /> Back to site
          </Link>
          <form
            data-testid="admin-login-form"
            onSubmit={onLogin}
            className="bg-[#2E211A]/80 backdrop-blur-xl border border-[#C85A32]/30 rounded-2xl p-8 sm:p-10 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.55)]"
          >
            <span className="w-12 h-12 rounded-full border border-[#C85A32]/40 flex items-center justify-center text-[#E89D4B] mb-6">
              <Lock size={18} />
            </span>
            <h1 className="font-display text-3xl font-semibold text-[#F7F2EB] mb-2">
              Dispatch <span className="italic text-[#E89D4B]">Login</span>
            </h1>
            <p className="text-sm text-[#E6DACB]/70 mb-8">Quote inbox access for Desert Valley Freight.</p>
            <div className="space-y-5">
              <div>
                <label htmlFor="admin-email" className="block font-mono text-[10px] tracking-[0.22em] uppercase text-[#E89D4B] mb-2">Email</label>
                <input id="admin-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@desertvalleyfreight.com" className={inputCls} />
              </div>
              <div>
                <label htmlFor="admin-password" className="block font-mono text-[10px] tracking-[0.22em] uppercase text-[#E89D4B] mb-2">Password</label>
                <input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} />
              </div>
              {authError && (
                <p data-testid="admin-login-error" className="text-sm text-[#DA6232]">{authError}</p>
              )}
              <button
                type="submit"
                data-testid="admin-login-submit"
                disabled={sending}
                className="w-full bg-[#C85A32] text-[#F7F2EB] font-mono text-xs tracking-[0.18em] uppercase rounded-full px-8 py-4 hover:bg-[#E89D4B] hover:text-[#1E1511] disabled:opacity-60 transition-colors duration-300"
              >
                {sending ? "Signing in…" : "Sign In"}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="admin-inbox" className="min-h-screen px-6 lg:px-10 py-12 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <div>
          <Link to="/" data-testid="admin-inbox-back-link" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-[#E6DACB]/60 hover:text-[#E89D4B] transition-colors duration-300 mb-4">
            <ArrowLeft size={13} /> Back to site
          </Link>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-[#F7F2EB]">
            Quote <span className="italic text-[#E89D4B]">Inbox</span>
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => loadQuotes(token)}
            data-testid="admin-refresh-button"
            disabled={loading}
            className="flex items-center gap-2 border border-[#C85A32]/40 text-[#E6DACB] font-mono text-[11px] tracking-[0.15em] uppercase rounded-full px-5 py-2.5 hover:border-[#E89D4B] hover:text-[#E89D4B] disabled:opacity-60 transition-colors duration-300"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            onClick={onLogout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 bg-[#C85A32] text-[#F7F2EB] font-mono text-[11px] tracking-[0.15em] uppercase rounded-full px-5 py-2.5 hover:bg-[#E89D4B] hover:text-[#1E1511] transition-colors duration-300"
          >
            <LogOut size={13} />
            Sign Out
          </button>
        </div>
      </div>

      {quotes === null ? (
        <p data-testid="admin-loading" className="font-mono text-xs tracking-[0.2em] uppercase text-[#E6DACB]/50 py-20 text-center">Loading quote requests…</p>
      ) : quotes.length === 0 ? (
        <div data-testid="admin-empty" className="text-center py-24 border border-dashed border-[#C85A32]/30 rounded-2xl">
          <Inbox size={32} className="mx-auto text-[#C85A32] mb-4" />
          <p className="font-display text-2xl text-[#F7F2EB] mb-2">No quote requests yet</p>
          <p className="text-sm text-[#E6DACB]/60">New submissions from the website will appear here.</p>
        </div>
      ) : (
        <div data-testid="admin-quotes-list" className="space-y-4">
          {quotes.map((q) => (
            <article
              key={q.quote_id}
              data-testid={`quote-item-${q.quote_id}`}
              className="bg-[#2E211A]/80 border border-[#C85A32]/25 rounded-xl p-6 sm:p-8 transition-[border-color] duration-300 hover:border-[#E89D4B]/60"
            >
              <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
                <div>
                  <h2 className="font-display text-xl sm:text-2xl font-semibold text-[#F7F2EB]">
                    {q.contact_name}
                    {q.company_name ? <span className="text-[#E6DACB]/60 font-normal"> — {q.company_name}</span> : null}
                  </h2>
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#E89D4B] mt-1">{fmtDate(q.created_at)}</p>
                </div>
                <span className={`font-mono text-[10px] tracking-[0.2em] uppercase rounded-full px-4 py-1.5 border ${q.lift_gate_required ? "border-[#E89D4B] text-[#E89D4B]" : "border-[#E6DACB]/30 text-[#E6DACB]/60"}`}>
                  {q.lift_gate_required ? "Lift Gate" : "No Lift Gate"}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-sm">
                <p className="text-[#E6DACB]/85"><span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#C85A32] block mb-1">Phone</span><a href={`tel:${q.phone}`} className="hover:text-[#E89D4B] transition-colors duration-300">{q.phone}</a></p>
                <p className="text-[#E6DACB]/85"><span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#C85A32] block mb-1">Email</span><a href={`mailto:${q.email}`} className="hover:text-[#E89D4B] transition-colors duration-300 break-all">{q.email}</a></p>
                <p className="text-[#E6DACB]/85"><span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#C85A32] block mb-1">Cargo</span>{q.cargo_type}{q.weight_lbs ? ` · ${q.weight_lbs.toLocaleString()} lbs` : ""}</p>
                <p className="text-[#E6DACB]/85"><span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#C85A32] block mb-1">Pickup</span>{q.pickup_address}</p>
                <p className="text-[#E6DACB]/85"><span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#C85A32] block mb-1">Delivery</span>{q.delivery_address}</p>
                <p className="text-[#E6DACB]/85"><span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#C85A32] block mb-1">Requested For</span>{q.preferred_date ? fmtDate(q.preferred_date) : "Flexible"}</p>
              </div>
              {q.notes && (
                <p className="mt-5 text-sm text-[#E6DACB]/70 border-t border-[#C85A32]/20 pt-4">
                  <Package size={13} className="inline mr-2 text-[#E89D4B]" />{q.notes}
                </p>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
